import type { User } from '@supabase/supabase-js'

// In memory only, except one sessionStorage hop across Google OAuth. Marketing imports this, so it must not import supabase or auth.
export interface Acquisition {
  source?: string
  referrer?: string
  landing?: string
  taste?: string
}

const OAUTH_HOP_KEY = 'recall_acq_oauth'
// Older OAuth accounts must not get today's source stamped on them.
const FRESH_ACCOUNT_MS = 15 * 60 * 1000

let firstTouch: Acquisition | null = null

const clean = (value: string | null | undefined, max: number): string | undefined => {
  const v = value?.trim().slice(0, max)
  return v ? v : undefined
}

const TASTE_SLUG_RE = /^[a-z0-9-]{1,80}$/
const cleanTaste = (value: string | null): string | undefined =>
  value && TASTE_SLUG_RE.test(value) ? value : undefined

const isOwnHost = (host: string, currentHost: string) =>
  host === currentHost || host === 'bumpkinlabs.com' || host.endsWith('.bumpkinlabs.com')

export function deriveAcquisition(search: string, referrer: string, pathname: string, currentHost: string): Acquisition {
  const params = new URLSearchParams(search)
  // Rides with whichever source wins, not instead of it.
  const taste = cleanTaste(params.get('taste'))
  const carried: Acquisition = {
    source: clean(params.get('acq_source'), 64),
    referrer: clean(params.get('acq_referrer'), 128),
    landing: clean(params.get('acq_landing'), 128),
  }
  if (carried.source || carried.referrer || carried.landing) return { ...carried, taste }

  let referrerHost: string | undefined
  try {
    const host = referrer ? new URL(referrer).hostname : ''
    referrerHost = host && !isOwnHost(host, currentHost) ? clean(host, 128) : undefined
  } catch {
    referrerHost = undefined
  }
  return {
    source: clean(params.get('utm_source'), 64),
    referrer: referrerHost,
    landing: clean(pathname, 128),
    taste,
  }
}

/** Call once per page load, before the router touches the URL. Later calls keep the first touch. */
export function captureFirstTouch(): void {
  if (firstTouch || typeof window === 'undefined') return
  firstTouch = deriveAcquisition(window.location.search, document.referrer, window.location.pathname, window.location.hostname)
}

export function getAcquisition(): Acquisition | null {
  return firstTouch
}

export function withAcquisitionParams(href: string, acq: Acquisition | null): string {
  if (!acq) return href
  const url = new URL(href, 'http://placeholder')
  if (acq.source) url.searchParams.set('acq_source', acq.source)
  if (acq.referrer) url.searchParams.set('acq_referrer', acq.referrer)
  if (acq.landing) url.searchParams.set('acq_landing', acq.landing)
  return url.origin === 'http://placeholder' ? `${url.pathname}${url.search}${url.hash}` : url.toString()
}

/** Marketing only. Click-time, not render-time: a render-time href would not survive prerender hydration. */
export function decorateAppLinksOnClick(appOrigin: string): void {
  if (!appOrigin || typeof document === 'undefined') return
  const decorate = (event: Event) => {
    const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null
    if (anchor && anchor.href.startsWith(appOrigin)) anchor.href = withAcquisitionParams(anchor.href, firstTouch)
  }
  document.addEventListener('click', decorate, true)
  document.addEventListener('auxclick', decorate, true)
}

export function stashForOAuthHop(): void {
  if (!firstTouch) return
  try { sessionStorage.setItem(OAUTH_HOP_KEY, JSON.stringify(firstTouch)) } catch { /* storage blocked */ }
}

function takeOAuthHop(): Acquisition | null {
  try {
    const raw = sessionStorage.getItem(OAUTH_HOP_KEY)
    sessionStorage.removeItem(OAUTH_HOP_KEY)
    return raw ? (JSON.parse(raw) as Acquisition) : null
  } catch {
    return null
  }
}

/** After the Google OAuth return: write the source only onto an account created moments ago. */
export async function recordAfterOAuth(
  user: User | null | undefined,
  updateMetadata: (data: { acquisition: Acquisition }) => Promise<unknown>,
  now = Date.now(),
): Promise<void> {
  const acq = takeOAuthHop()
  if (!acq || !user || user.user_metadata?.acquisition) return
  if (now - new Date(user.created_at).getTime() > FRESH_ACCOUNT_MS) return
  await updateMetadata({ acquisition: acq })
}

export function _resetAcquisitionForTest(value: Acquisition | null = null): void {
  firstTouch = value
}
